import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Download, 
  FileText, 
  Building2, 
  Landmark,
  FolderOpen
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', label: 'Tous', icon: FolderOpen },
  { id: 'mairie', label: 'Mairie', icon: Building2 },
  { id: 'administration', label: 'Administration', icon: FileText },
  { id: 'prefecture', label: 'Préfecture', icon: Landmark },
  { id: 'autre', label: 'Autres', icon: FileText }
];

export default function Tools() {
  const [templates, setTemplates] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (template) => {
    try {
      await axios.post(`${API}/templates/${template.id}/download`);
      // In a real app, this would trigger the actual download
      if (template.file_url && template.file_url !== '#') {
        window.open(template.file_url, '_blank');
      } else {
        toast.info('Ce document sera bientôt disponible au téléchargement.');
      }
    } catch (error) {
      console.error('Failed to log download:', error);
    }
  };

  const filteredTemplates = activeCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === activeCategory);

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.icon : FileText;
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.label : category;
  };

  return (
    <div className="min-h-screen" data-testid="tools-page">
      {/* Header */}
      <div className="bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Outils & Courriers types
          </h1>
          <p className="text-muted-foreground">
            Modèles de courriers prêts à l'emploi pour vos démarches administratives.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info banner */}
        <Card className="mb-8 bg-accent/30 border-accent">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Comment utiliser ces modèles ?</h3>
                <p className="text-sm text-muted-foreground">
                  Ces courriers sont des modèles à adapter à votre situation personnelle. 
                  Personnalisez-les avec vos informations (nom, adresse, dates, références de dossier) 
                  et adaptez le contenu en fonction de votre cas particulier.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Categories Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="mb-6 flex-wrap h-auto gap-2">
            {categories.map((cat) => (
              <TabsTrigger 
                key={cat.id} 
                value={cat.id}
                className="gap-2"
                data-testid={`tab-${cat.id}`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeCategory}>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardHeader>
                      <div className="h-4 bg-muted rounded w-3/4" />
                    </CardHeader>
                    <CardContent>
                      <div className="h-16 bg-muted rounded" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredTemplates.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Aucun modèle</h3>
                  <p className="text-muted-foreground">
                    Aucun modèle n'est disponible dans cette catégorie pour le moment.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredTemplates.map((template) => {
                  const Icon = getCategoryIcon(template.category);
                  return (
                    <Card key={template.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${template.id}`}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-sm bg-accent flex items-center justify-center">
                              <Icon className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <CardTitle className="text-lg">{template.title}</CardTitle>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline">{getCategoryLabel(template.category)}</Badge>
                                <Badge variant="secondary">{template.file_type.toUpperCase()}</Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-4">{template.description}</CardDescription>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {template.download_count} téléchargements
                          </span>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="gap-2"
                            onClick={() => handleDownload(template)}
                            data-testid={`download-${template.id}`}
                          >
                            <Download className="w-4 h-4" />
                            Télécharger
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Additional resources */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Besoin d'un modèle spécifique ?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Si vous ne trouvez pas le modèle adapté à votre situation, 
                notre assistant IA peut vous aider à rédiger un courrier personnalisé.
              </p>
              <Button variant="outline" className="mt-4" asChild>
                <a href="/assistant">Demander à l'assistant</a>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Partager vos modèles</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Vous avez rédigé des courriers qui ont fonctionné ? 
                Partagez-les avec la communauté pour aider d'autres personnes.
              </p>
              <Button variant="outline" className="mt-4" disabled>
                Proposer un modèle
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
